<script>
	import { normalizeCollection, playerUrl } from './shared.js';
	import GameTeamComparison from './GameTeamComparison.svelte';

	export let pregame = null;
	export let team1 = null;
	export let team2 = null;
	export let tag = 'mlbl';
	export let compId = '';

	$: team1Name = getTeamName(1);
	$: team2Name = getTeamName(2);
	$: lastGames1 = normalizeCollection(pregame?.lastGames1);
	$: lastGames2 = normalizeCollection(pregame?.lastGames2);
	$: gameHistory = normalizeCollection(pregame?.gameHistory);
	$: leaders1 = getLeaders(pregame?.teamStats1);
	$: leaders2 = getLeaders(pregame?.teamStats2);

	function getTeamName(side) {
		const calendar = side === 1 ? team1 : team2;
		const pre = side === 1 ? pregame?.teamStats1?.team : pregame?.teamStats2?.team;
		return calendar?.name || calendar?.shortName || calendar?.abcName || pre?.name || pre?.shortName || pre?.label || `Команда ${side}`;
	}

	function getLeaders(teamStats) {
		return normalizeCollection(teamStats?.players)
			.map((player) => {
				const lastName = player?.person?.lastName || '';
				const firstName = player?.person?.firstName || '';
				const games = Number(player?.games) || 0;
				const points = Number(player?.stats?.points) || 0;
				return {
					personId: player?.personId ?? player?.person?.id,
					photo: player?.person?.photo || '',
					name: [lastName, firstName].filter(Boolean).join(' '),
					initials: `${firstName[0] || ''}${lastName[0] || ''}`.trim() || 'P',
					games,
					points,
					avg: games > 0 ? points / games : 0
				};
			})
			.filter((leader) => leader.name)
			.sort((a, b) => b.avg - a.avg)
			.slice(0, 5);
	}

	function getPlayerHref(personId) {
		if (!personId) {
			return '';
		}
		return playerUrl(personId, { tag, compId });
	}

	function formatAvg(value) {
		const v = Number(value) || 0;
		return v > 0 ? v.toFixed(1) : '0';
	}

	function getHistoryDate(match) {
		return match?.game?.localDate || match?.game?.localTime || '—';
	}

	function getHistoryScore(match) {
		return `${match?.game?.score1 ?? '—'} : ${match?.game?.score2 ?? '—'}`;
	}
</script>

<div class="preview">
	{#if pregame}
		<div class="preview-section">
			<h4 class="preview-section__title">История встреч</h4>
			<div class="h2h">
				<div class="h2h__record">
					<div class="h2h__team h2h__team--left">
						<div class="h2h__team-name">{team1Name}</div>
						{#if lastGames1.length}
							<div class="h2h__form">
								{#each lastGames1 as win}
									<span class="form-dot" class:form-dot--win={win} class:form-dot--loss={!win}></span>
								{/each}
							</div>
						{/if}
					</div>

					<div class="h2h__wins">
						<b>{pregame.team1Win ?? 0}</b>
						<span class="h2h__wins-sep">:</span>
						<b>{pregame.team2Win ?? 0}</b>
					</div>

					<div class="h2h__team h2h__team--right">
						<div class="h2h__team-name">{team2Name}</div>
						{#if lastGames2.length}
							<div class="h2h__form">
								{#each lastGames2 as win}
									<span class="form-dot" class:form-dot--win={win} class:form-dot--loss={!win}></span>
								{/each}
							</div>
						{/if}
					</div>
				</div>

				{#if gameHistory.length}
					<div class="h2h__history">
						{#each gameHistory as match}
							<div class="h2h__game">
								<span class="h2h__game-date">{getHistoryDate(match)}</span>
								<span class="h2h__game-score">{getHistoryScore(match)}</span>
							</div>
						{/each}
					</div>
				{/if}
			</div>
		</div>

		<div class="preview-section">
			<h4 class="preview-section__title">Лидеры по очкам</h4>
			<div class="leaders">
				<div class="leaders__column">
					<div class="leaders__team-title">{team1Name}</div>
					{#each leaders1 as leader}
						<a class="leader" href={getPlayerHref(leader.personId) || undefined}>
							{#if leader.photo}
								<img class="leader__photo" src={leader.photo} alt={leader.name}>
							{:else}
								<div class="leader__photo leader__photo--placeholder">{leader.initials}</div>
							{/if}
							<div class="leader__body">
								<div class="leader__name">{leader.name}</div>
								<div class="leader__avg">{formatAvg(leader.avg)} очков за игру</div>
							</div>
						</a>
					{/each}
				</div>

				<div class="leaders__column">
					<div class="leaders__team-title">{team2Name}</div>
					{#each leaders2 as leader}
						<a class="leader" href={getPlayerHref(leader.personId) || undefined}>
							{#if leader.photo}
								<img class="leader__photo" src={leader.photo} alt={leader.name}>
							{:else}
								<div class="leader__photo leader__photo--placeholder">{leader.initials}</div>
							{/if}
							<div class="leader__body">
								<div class="leader__name">{leader.name}</div>
								<div class="leader__avg">{formatAvg(leader.avg)} очков за игру</div>
							</div>
						</a>
					{/each}
				</div>
			</div>
		</div>

		<div class="preview-section">
			<h4 class="preview-section__title">Сравнение в турнире</h4>
			<GameTeamComparison team1={pregame?.teamStats1?.totalStats} team2={pregame?.teamStats2?.totalStats} />
		</div>
	{:else}
		<div class="preview__empty">Нет данных для превью</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.preview {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}
	.preview-section__title {
		margin: 0 0 12px;
		font-size: 16px;
		font-weight: 700;
		color: #122033;
	}
	.preview__empty {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 88px;
		color: #53657b;
		text-align: center;
	}

	.h2h {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
	.h2h__record {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		gap: 12px;
		padding: 12px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.h2h__team--left { text-align: left; }
	.h2h__team--right { text-align: right; }
	.h2h__team-name {
		font-size: 14px;
		font-weight: 700;
		color: #122033;
	}
	.h2h__form {
		display: flex;
		gap: 4px;
		margin-top: 6px;
	}
	.h2h__team--right .h2h__form {
		justify-content: flex-end;
	}
	.form-dot {
		width: 10px;
		height: 10px;
		border-radius: 999px;
	}
	.form-dot--win { background: #22a55b; }
	.form-dot--loss { background: #e0524d; }
	.h2h__wins {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 22px;
		font-weight: 800;
		color: #122033;
	}
	.h2h__wins-sep { color: #6b7a90; }
	.h2h__history {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}
	.h2h__game {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 7px 10px;
		border-radius: 8px;
		background: #fff;
		border: 1px solid #eef1f6;
		font-size: 13px;
		color: #53657b;
	}
	.h2h__game-score {
		font-weight: 700;
		color: #122033;
	}

	.leaders {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
	}
	.leaders__column {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.leaders__team-title {
		margin-bottom: 2px;
		font-size: 13px;
		font-weight: 700;
		color: #6b7a90;
	}
	.leader {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px;
		border-radius: 10px;
		background: #f7f9fc;
		text-decoration: none;
		color: inherit;
	}
	a.leader:hover {
		background: #eef3fb;
	}
	.leader__photo {
		width: 40px;
		height: 40px;
		border-radius: 7px;
		object-fit: cover;
		background: #e6ecf4;
		flex: 0 0 40px;
	}
	.leader__photo--placeholder {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-size: 13px;
		font-weight: 700;
		color: #00244d;
	}
	.leader__body {
		min-width: 0;
	}
	.leader__name {
		font-size: 13px;
		font-weight: 700;
		color: #122033;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.leader__avg {
		margin-top: 2px;
		font-size: 12px;
		color: #6b7a90;
	}

	@media (max-width: 640px) {
		.leaders { grid-template-columns: 1fr; }
	}
</style>
